/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import {
  applyDefaultOffOptIn,
  EnabledPluginsContext,
  filterPluginsByReleaseFlags,
  listConsoleProviders,
  presenceIds,
  resolveEnabledPlugins,
  subtractDisabledPlugins,
} from '@aglyn/aglyn'
import { useUser } from '@aglyn/tenant-feature-instance'
import type React from 'react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import BootSplash from './boot-splash.component'
import { consolePluginsAt } from '../constants/console-plugin-load-points'
import {
  consolePluginLoader,
  pluginDeclarationsReady,
} from '../constants/console-plugin-loader'
import {
  useHostDisabledPlugins,
  useHostEnabledPlugins,
  useHostId,
} from './host-id-provider'
import usePermissionsOnHost from '../hooks/use-permissions-on-host'
import useCurrentOrg from '../hooks/use-current-org'
import { useReleaseFlags } from '../hooks/use-release-flags'
import { useUrlNamedOrg, useUrlNamesOrg } from '../hooks/use-url-names-org'
import {
  loadOrgRealmPlugins,
  prefetchOrgRealmInstalls,
} from '../utils/realm-plugins.client'

/**
 * The workspace's EFFECTIVE plugin set (AGL-416/422): the org switchboard
 * minus release-flagged-off plugins (staff keep flagged plugins — the
 * usual staff-preview bypass). Returned as a stable comma key; loading
 * waits for Remote Config activation so a kill-switched plugin never
 * flashes in on the registry defaults.
 */
function useEffectiveEnabledPlugins(): { flagsReady: boolean; enabledKey: string } {
  // EXEMPT from `no-unguarded-loading-hook` (AGL-1422), and the one place in
  // this sweep where adding the gate would be the more dangerous change.
  //
  // `resolveEnabledPlugins` fails OPEN: an undefined `org` yields the DEFAULT
  // plugin set, so the loading window can only ever show MORE than the
  // workspace enabled — never the refusal the rule exists to stop. Folding
  // `ready` into `flagsReady` would put the entire plugin surface (nav,
  // pages, providers, and via `useSitePluginsReady` the besigner canvas)
  // behind the billing doc — and `useConfirmedDoc` leaves `ready` false
  // indefinitely when a MISSING doc's server confirm cannot complete, which
  // is a plausible state for a pre-billing workspace. That trades a
  // cosmetic fail-open for a console with no plugins in it at all.
  //
  // The residue is a beat of the default nav on a cold load, which the
  // BootSplash hold below already covers on a session's first load. Revisit
  // only if `resolveEnabledPlugins` ever stops defaulting.
  // eslint-disable-next-line aglyn/no-unguarded-loading-hook
  const { org } = useCurrentOrg()
  const { ready, isStaff, flags } = useReleaseFlags()
  const enabledKey = filterPluginsByReleaseFlags(
    resolveEnabledPlugins(org),
    (flagKey) => flags[flagKey as keyof typeof flags]?.released ?? true,
    { staffBypass: isStaff },
  ).join(',')
  return { flagsReady: ready, enabledKey }
}

/**
 * The current workspace's effective plugin ids, for scoping reads of the
 * ConsoleExtension registry (AGL-758). The registry is a module-global
 * union across every org visited this session, so any surface that lists
 * nav items, widgets, pages or providers must filter by this set — without
 * it, a plugin enabled for one workspace keeps contributing to the next.
 */
export function useEnabledPluginIds(): string[] {
  const { enabledKey } = useEffectiveEnabledPlugins()
  // Per-site enablement (AGL-1014): inside a host route the site's
  // `disabledPlugins` deny-list is subtracted, so nav tabs, plugin pages
  // and widget slots all read the per-site set. [] off host routes.
  const hostDisabled = useHostDisabledPlugins()
  // The per-site OPT-IN half (AGL-2486). Subtracting a deny-list is the
  // whole story for the twelve ordinary bundles and exactly wrong for a
  // `defaultOffPerSite` one: an absent field means OFF for `accounts`, so a
  // resolver that only ever subtracts reported it as available on every site
  // that had never mentioned it — which is how the besigner went on offering
  // Members blocks for a site whose /signin returns 404.
  //
  // Applied only INSIDE a host route. Off one there is no site to have opted
  // in, and an empty opt-in list would subtract rather than no-op — unlike
  // the deny-list above, where [] already means "narrow nothing". Answering
  // a host question on a page that names no host is what AGL-2486 fixed
  // immediately below for the org set; this keeps the same discipline.
  const hostOptIn = useHostEnabledPlugins()
  const hostId = useHostId()
  // There is no "current workspace's" plugin set on a route that names no
  // workspace (AGL-2486). `useEffectiveEnabledPlugins` reads `useCurrentOrg`,
  // which falls back to a remembered org, so this answered with THAT org's
  // plugins off org routes. The gate below already refuses to LOAD plugins
  // there, but the registry is a session-wide union — so a staff user who
  // visited their own workspace first saw their own entitled widgets on
  // `/admin/orgs/{someone-else}`, a page about a different org entirely.
  // Gating here rather than at each consumer is the same reasoning as the
  // analytics mount gate: a filter every lister must remember to apply is a
  // filter that will eventually be forgotten.
  const namedOrg = useUrlNamedOrg()
  return useMemo(
    () =>
      namedOrg
        ? subtractDisabledPlugins(
            hostId
              ? applyDefaultOffOptIn(
                  enabledKey.split(',').filter(Boolean),
                  hostOptIn,
                )
              : enabledKey.split(',').filter(Boolean),
            hostDisabled,
          )
        : [],
    [enabledKey, hostDisabled, hostOptIn, hostId, namedOrg],
  )
}

/**
 * The workspace's effective plugin ids WITHOUT the per-site narrowing
 * (AGL-3028) — for what wraps every console page rather than what a page
 * draws.
 *
 * Providers are nested around the whole route tree, so the list of them is
 * part of the tree's SHAPE: a provider present on one page and absent on the
 * next unmounts and remounts everything beneath it, the app bar and every
 * open listener included. Narrowed per site, that would happen on every
 * crossing between a site that switched a plugin off and one that did not —
 * and on every load of such a site, as its host document lands after the
 * first render. So providers are listed against the workspace, and each is
 * handed the site's narrowed set to decide what it opens there.
 */
export function useWorkspacePluginIds(): string[] {
  const { enabledKey } = useEffectiveEnabledPlugins()
  // The same no-workspace-no-plugins rule as `useEnabledPluginIds` (AGL-2486).
  const namedOrg = useUrlNamedOrg()
  return useMemo(
    () => (namedOrg ? enabledKey.split(',').filter(Boolean) : []),
    [enabledKey, namedOrg],
  )
}

/**
 * What a screen needs to load plugins: the workspace it may load for — `null`
 * while it may not — and the account the realm fetch authorizes as
 * (AGL-3142).
 *
 * The conditions the gate below applies before it loads anything, in one
 * answer, because every other place that loads a plugin owes the same ones
 * and none of them is obvious:
 *
 * - the URL must NAME a workspace (AGL-1937). `useCurrentOrg` falls back to a
 *   remembered selection, so a staff page or the workspace picker would
 *   otherwise fetch the realm installs of an organization it is not about;
 * - the org must have resolved, or there is no workspace to load for;
 * - Remote Config must have activated (AGL-422), or a release-flagged-off
 *   plugin loads on the registry defaults and sticks, because a loaded chunk
 *   cannot unload.
 *
 * Here rather than in the loading hooks so a zone and a plugin route reach
 * all of it through the module they already read their plugin set from — one
 * seam to hold, and one to stand in for when a surface is put under test.
 */
export function usePluginLoadScope(): {
  orgId: string | null
  user: ReturnType<typeof useUser>['data']
} {
  const { orgId } = useCurrentOrg()
  const { data: user } = useUser()
  const namesOrg = useUrlNamesOrg()
  const { ready: flagsReady } = useReleaseFlags()
  return {
    orgId: namesOrg && flagsReady ? (orgId ?? null) : null,
    user,
  }
}

/**
 * Dynamic console-plugin activation (AGL-417), replacing the static
 * register-console-plugins composition root: once the org workspace
 * resolves, load + register its enabled plugins' ConsoleExtensions, THEN
 * render the shell — nav items and plugin pages come from the registry, so
 * rendering earlier would drop them. Signed-out surfaces (no org) render
 * immediately; a workspace's first paint waits one cached chunk-load.
 */
export default function ConsolePluginsGate({
  children,
}: {
  children?: ReactNode
}) {
  const { org, orgId, ready: orgReady } = useCurrentOrg()
  const { data: user } = useUser()
  // Only the URL can say which workspace the session belongs to (AGL-1937).
  // See the loading effect below for why this gate is here.
  const namesOrg = useUrlNamesOrg()
  const [readyForOrg, setReadyForOrg] = useState<string | null>(null)
  const { flagsReady, enabledKey } = useEffectiveEnabledPlugins()
  const enabledPluginIds = useEnabledPluginIds()
  const workspacePluginIds = useWorkspacePluginIds()
  // The reader's plugin permissions on the site in view (AGL-2927,
  // AGL-2984), resolved ONCE here for every provider rather than by each — a
  // plugin package cannot reach the console's permission context, and a
  // provider whose doors spend needs the answer to hold them closed.
  const hostId = useHostId()
  const permissionsOnHost = usePermissionsOnHost(hostId)
  // Latches on the first completed load; from then on a workspace switch
  // renders through instead of blanking the tree (AGL-758).
  const hasLoadedOnce = useRef(false)
  if (readyForOrg) hasLoadedOnce.current = true
  // The plugins' declarations (AGL-2939) load with the shell, and every
  // route reads them at render — the staff lockdown page lists a plugin's
  // levers, a billing page folds its add-on — so the first paint of ANY
  // route waits for them, org-less ones included. Settled, not succeeded: a
  // declarations chunk that fails to load is logged and the console renders
  // without it rather than never.
  const [declarationsSettled, setDeclarationsSettled] = useState(false)
  useEffect(() => {
    let active = true
    void pluginDeclarationsReady
      .catch((error) => console.error('plugin declarations failed to load', error))
      .then(() => {
        if (active) setDeclarationsSettled(true)
      })
    return () => {
      active = false
    }
  }, [])

  // The realm install list, started the moment the workspace is known
  // (AGL-3660). It needs neither the release flags nor the first-party chunks
  // the effect below waits on, so it no longer queues behind them: the effect
  // below finds the request in flight. Same rule as below, though: nothing
  // for a route that names no workspace (AGL-1937).
  useEffect(() => {
    if (!namesOrg || !orgId) return
    prefetchOrgRealmInstalls(orgId, user)
    // `user` identity churns with token refreshes; orgId names the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesOrg, orgId])

  useEffect(() => {
    // Nothing loads until the URL names a workspace (AGL-1937).
    //
    // This gate is mounted in `app/providers.tsx`, above EVERY console route
    // — the workspace picker included, which every new signup crosses. On an
    // org-less route `useCurrentOrg().orgId` is the ambient fallback (a
    // remembered selection, else the user's FIRST org), so merely landing on
    // the picker used to `ensure` that org's console chunks, mint an ID token
    // and fetch its trusted-realm marketplace bundles — for a workspace
    // nobody had opened, moments before the user picks a different one.
    //
    // That is not a wasted fetch a later navigation corrects: a loaded chunk
    // cannot unload and the ConsoleExtension registry is a module-global
    // union, so the wrong org's code stays resident for the session.
    //
    // "Don't start on an org-less route" rather than "reload per org": the
    // AGL-758 latch below exists precisely so a workspace switch does not
    // blank the tree, and unloading is not on the table.
    //
    // `useUrlNamesOrg` is derived from the URL alone, so it has no loading
    // window of its own — it answers false for "this route names no
    // workspace", never for "not read yet" (AGL-1113).
    //
    // Wait for Remote Config activation (AGL-422) so a release-flagged-off
    // plugin never loads on the registry defaults and then sticks (loaded
    // chunks can't unload).
    if (!namesOrg || !orgId || !flagsReady) return undefined
    let active = true
    void (async () => {
      // Only what the shell itself draws (AGL-3142): a nav tab, an
      // organization tab, a staff tab, a provider. A plugin whose
      // contributions are a zone or a route loads where that zone is
      // rendered or that route is served — `useConsoleSlotPlugins` and
      // `useConsoleRoutePlugins` — and a plugin that declares nothing keeps
      // loading here, because a nav tab is only discoverable by running
      // `register()`.
      await consolePluginLoader.ensure(
        consolePluginsAt(enabledKey.split(','), { at: 'shell' }),
        ['console'],
      )
      // Trusted-realm marketplace plugins (AGL-420): loaded after the
      // first-party set so their registrations land before the shell
      // renders, and by the same rule — the installs that draw something on
      // every screen, never the whole install list. Failures inside are
      // logged and skipped; a broken remote bundle never blocks the console.
      await loadOrgRealmPlugins(orgId, user, { at: 'shell' })
      if (active) setReadyForOrg(orgId)
    })()
    return () => {
      active = false
    }
    // `user` identity churns with token refreshes; orgId names the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesOrg, orgId, flagsReady, enabledKey])

  // This gate sits ABOVE the whole route tree — the app bar and nav strip
  // included — so holding here blanks the entire window, not just the page
  // body. It used to hold on every `orgId` CHANGE, which meant a workspace
  // switch unmounted and remounted the whole console: an empty frame, then
  // the boot splash, then a fresh tree (AGL-758). The hold spans a network
  // fetch (`loadOrgRealmPlugins`), so on a cold cache that was seconds of it
  // (AGL-903).
  //
  // Only the FIRST load of a session needs the hold, because the registry
  // starts empty and rendering the shell before it is populated would drop
  // every plugin nav item and page. After that the registry is a union that
  // never shrinks, and reads are scoped to the org's own plugin ids
  // (`useEnabledPluginIds`), so a switch can keep the previous tree mounted:
  // the new workspace's plugin tabs simply appear when their chunks land,
  // and the old workspace's never leak in.
  //
  // An org-less route holds for nothing (AGL-1937): the load never starts
  // there, so without `namesOrg` the picker would sit behind this splash
  // waiting on a `readyForOrg` that can never arrive.
  if (
    !declarationsSettled ||
    (namesOrg && orgId && !hasLoadedOnce.current && readyForOrg !== orgId)
  ) {
    return <BootSplash />
  }
  // Plugin-registered app providers (AGL-419) wrap every console page —
  // e.g. the marketplace plugin's AI-assist provider. Scoped to this org, so
  // a provider from a previously visited workspace does not linger.
  //
  // `orgReady` rides along with `org` (AGL-1380). This gate sits above the
  // whole route tree, so unlike the plugin-page route it CANNOT hold its
  // render until the billing doc settles — that would blank the console on
  // every load. A provider's entitlement gate therefore has to be told the
  // difference between "not on your plan" and "no answer yet", because an
  // undefined `org` checks as the free tier either way.
  //
  // Listed against the WORKSPACE, and handed the SITE's set (AGL-3028): see
  // `useWorkspacePluginIds` for why a per-site list would remount the tree.
  // A provider whose plugin a site switched off stays mounted there and opens
  // nothing — `enabledPluginIds` is how it knows.
  return listConsoleProviders(workspacePluginIds).reduce<ReactNode>(
    (inner, Provider, index) => (
      <Provider
        key={index}
        org={org}
        orgReady={orgReady}
        orgId={orgId}
        hostId={hostId}
        permissionsOnHost={permissionsOnHost}
        enabledPluginIds={enabledPluginIds}
      >
        {inner}
      </Provider>
    ),
    children,
  )
}

/**
 * Editor-surface gate (AGL-417): besigner/preview canvases additionally
 * need the enabled plugins' SITE components (canvas bundles). Returns true
 * once they're registered — callers must not mount the canvas before then
 * (the blank-canvas invariant, AGL-52).
 */
/**
 * HOC form of the editor-surface gate: renders nothing until the enabled
 * plugins' site components are registered, then mounts the wrapped page.
 * Used on the besigner/preview pages so their hook-heavy bodies never run
 * against an empty component registry.
 *
 * It is also where the editor learns the site's plugin set (AGL-1014).
 * `consolePluginLoader` never unloads a bundle and the preset registry is a
 * module-global union, so a plugin loaded while editing one site keeps
 * registering palette entries on the next — narrowing what gets LOADED
 * cannot take back what is already registered. The component drawer
 * therefore FILTERS on this set, and it is published here rather than per
 * route so no editor page can be added that forgets.
 */
export function withSitePlugins<P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> {
  function WithSitePlugins(props: P) {
    const { ready, realmPluginIds } = useSitePlugins()
    const siteEnabledPluginIds = useEnabledPluginIds()
    // A marketplace plugin the site runs is on the site's list too, under
    // every id its presets may carry (AGL-3391): the switchboard's list names
    // first-party plugins, and the drawer would otherwise hide the elements
    // this gate just loaded.
    const enabledPluginIds = useMemo(
      () =>
        realmPluginIds.length
          ? [...new Set([...siteEnabledPluginIds, ...realmPluginIds])]
          : siteEnabledPluginIds,
      [siteEnabledPluginIds, realmPluginIds],
    )
    if (!ready) return null
    return (
      <EnabledPluginsContext.Provider value={enabledPluginIds}>
        <Component {...props} />
      </EnabledPluginsContext.Provider>
    )
  }
  WithSitePlugins.displayName = `WithSitePlugins(${Component.displayName ?? Component.name ?? 'Page'})`
  return WithSitePlugins
}

export function useSitePluginsReady(): boolean {
  return useSitePlugins().ready
}

const NO_REALM_PLUGINS: readonly string[] = []

/**
 * The editor surface's plugins: the site's first-party canvas bundles, and the
 * marketplace installs the site runs that declare site elements or features
 * (AGL-3391), loaded together so the canvas never mounts ahead of either.
 * `realmPluginIds` are the ids those installs' presets may carry.
 *
 * The installs are the site's list, the one its published pages load from:
 * the workspace's pins with the site's own on top. A realm load never throws
 * and never holds the canvas past settling; a bundle that fails to load is
 * logged and its elements are simply absent, as on the published page.
 */
function useSitePlugins(): {
  ready: boolean
  realmPluginIds: readonly string[]
} {
  const { orgId } = useCurrentOrg()
  const { user } = usePluginLoadScope()
  const hostId = useHostId()
  // Same fallback-org gate as the console gate above (AGL-1937). Every
  // `withSitePlugins` route lives under `/[orgSlug]/hosts/[host]/…`, so this
  // never withholds the canvas from a page that has one — the org-less
  // system-email editor deliberately calls `ensure` itself (AGL-759) rather
  // than resolving a plugin set from whichever org the scope fell back to.
  const namesOrg = useUrlNamesOrg()
  const [ready, setReady] = useState(false)
  const [realmPluginIds, setRealmPluginIds] =
    useState<readonly string[]>(NO_REALM_PLUGINS)
  const { flagsReady, enabledKey } = useEffectiveEnabledPlugins()

  useEffect(() => {
    if (!namesOrg || !orgId || !flagsReady) return undefined
    let active = true
    setReady(false)
    void Promise.all([
      consolePluginLoader.ensure(enabledKey.split(','), ['site']),
      hostId
        ? loadOrgRealmPlugins(orgId, user, { at: 'editor' }, { hostId })
        : Promise.resolve([]),
    ]).then(([, installs]) => {
      if (!active) return
      const ids = installs.flatMap((install) => presenceIds(install))
      setRealmPluginIds(ids.length ? ids : NO_REALM_PLUGINS)
      setReady(true)
    })
    return () => {
      active = false
    }
    // `user` identity churns with token refreshes; the org and the host name
    // the load, as they do for every other place the console loads plugins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesOrg, orgId, flagsReady, enabledKey, hostId])

  return { ready, realmPluginIds }
}
